import Vue from 'vue'
import crypto from 'crypto-js'
import request from './request'
import { text, rows, scopeKey, validateReport, flag, studyUuid } from './studyModel.mjs'

const root = '/api/v1/study/'
const pendingPrefix = 'sk-study-operation:'
const currentOwner = () => EUtils.getCookie('Authorization') || ''
export const pathId = (value) => encodeURIComponent(text(value))

// Persist only a digest and opaque operation key; retries reuse the original key.
async function send(path, method = 'get', data = {}, legacy = false, form = false) {
  const owner = currentOwner()
  if (!owner) throw new Error('请登录后使用学习计划和学习报告')
  const digest = crypto.SHA256(JSON.stringify([owner, path, data])).toString()
  const storageKey = pendingPrefix + digest
  let operationKey
  if (method === 'post' && !legacy) {
    try {
      operationKey = localStorage.getItem(storageKey) || 'study-' + studyUuid()
      localStorage.setItem(storageKey, operationKey)
    } catch (_) {
      throw new Error('无法保存操作标识，请允许浏览器本地存储后重试')
    }
  }
  const config = {
    url: Vue.prototype.dataurl + path,
    method,
    studyApi: true,
    timeout: 30000,
    headers: { app_id: '1' }
  }
  if (method === 'get') config.params = data
  else config.data = data
  if (operationKey) config.headers['Idempotency-Key'] = operationKey
  if (form) {
    if (process.env.NODE_ENV === 'development') {
      config.headers['Content-Type'] = 'application/json;charset=utf-8'
      config.transformRequest = [(body) => JSON.stringify(body)]
    } else {
      config.headers['Content-Type'] = 'application/x-www-form-urlencoded'
      config.transformRequest = [
        (body) =>
          Object.keys(body)
            .map((key) => encodeURIComponent(key) + '=' + encodeURIComponent(body[key]))
            .join('&')
      ]
    }
  }
  const release = () => {
    if (operationKey) localStorage.removeItem(storageKey)
  }
  try {
    const { body, status } = await request(config)
    if (owner !== currentOwner()) throw new Error('账号已切换，请刷新当前页面')
    const success =
      legacy || form
        ? Number(body.code) === 200
        : body.status === 'success' && !body.error && body.data && typeof body.data === 'object'
    if (!(status >= 200 && status < 300) || !success) {
      const error = new Error((body.error || {}).message || body.message || '服务返回内容不完整，请重试')
      error.code = (body.error || {}).code || body.code
      error.status = status
      error.retryable = (body.error || {}).retryable
      throw error
    }
    // An accepted write is confirmed only after its resource identity is usable.
    if (method === 'post' && !legacy && !form) {
      const data = body.data
      let complete = true
      if (path.endsWith('/previews')) complete = Boolean(data.preview_id && data.config)
      else if (path.endsWith('/sessions')) complete = Boolean((data.session || data).session_id)
      else if (path.endsWith('/ai-analyses'))
        complete = Boolean(
          data.job_id &&
            data.report_id &&
            ['queued', 'running', 'succeeded', 'failed', 'cancelled'].includes(data.status)
        )
      else if (path.endsWith('/ai-adjustments')) complete = Boolean(data.job_id && data.status)
      else if (path.endsWith('/practice-plans')) complete = Boolean(data.plan_id)
      else if (path.endsWith('/review-tasks')) complete = Boolean(data.source_plan_id)
      else if (path.endsWith('/answer-attempts/sync')) complete = Array.isArray(data.receipts)
      if (!complete) throw new Error('服务返回内容不完整，已保留本次操作标识，请重试')
    }
    release()
    return form ? body : legacy ? body.data || {} : body.data
  } catch (failure) {
    if (failure.response) {
      const body = failure.response.data || {}
      failure.status = failure.response.status
      failure.message = (body.error || {}).message || body.message || failure.message
      failure.code = (body.error || {}).code
      failure.retryable = (body.error || {}).retryable
      failure.requestId = body.request_id
    }
    if ([400, 403, 409, 410, 422].includes(failure.status) && !failure.retryable) release()
    if ([404, 405].includes(failure.status)) failure.message = '当前题库暂未开放此学习服务，请稍后重试'
    throw failure
  }
}
export const studyGet = (route, params = {}) => send(root + route, 'get', params)
export const studyPost = (route, data) => send(root + route, 'post', data)
function normalizeConfig(value = {}) {
  // Deployed responses include flat compatibility aliases. Do not send stale aliases
  // alongside an edited nested goal; the server correctly rejects conflicting values.
  const { period, objective_target_count, interleave_wrong, allocation_mode, ...config } = value
  return {
    ...config,
    ...(!config.goal && period != null && objective_target_count != null
      ? { goal: { period, objective_target_count } }
      : {}),
    include_wrong_review:
      config.include_wrong_review == null ? interleave_wrong : config.include_wrong_review,
    partial_period_policy:
      config.partial_period_policy == null ? allocation_mode : config.partial_period_policy
  }
}
function normalizePlan(value = {}) {
  const config = normalizeConfig(value.config)
  return {
    ...value,
    config,
    identity_parent_id: value.identity_parent_id || config.identity_parent_id,
    ...(value.pending_revision
      ? {
          pending_revision: {
            ...value.pending_revision,
            config: normalizeConfig(value.pending_revision.config)
          }
        }
      : {})
  }
}
function normalizeLearningResponse(data, route) {
  if (!data || typeof data !== 'object') throw new Error('计划返回内容不完整，请重试')
  if (route.endsWith('/history') && !data.items && Array.isArray(data.events))
    data.items = data.events.map((event) => ({
      ...event,
      kind: event.event_type,
      created_at: event.occurred_at
    }))
  if (data.pagination && data.pagination.has_more == null)
    data.pagination = { ...data.pagination, has_more: Boolean(data.pagination.next_cursor) }
  if (route.includes('/ai-adjustments')) {
    if (data.base_plan_version == null) data.base_plan_version = data.plan_version
    if (data.base_stats_version == null) data.base_stats_version = data.stats_version
    if (data.result) {
      const result = { ...data.result },
        config = result.proposed_config || {},
        goal = config.goal || {}
      if (
        (config.objective_target_count != null &&
          goal.objective_target_count != null &&
          Number(config.objective_target_count) !== Number(goal.objective_target_count)) ||
        (config.period != null && goal.period != null && config.period !== goal.period)
      )
        result._proposalIssue = '后台建议的周期或题量存在冲突，暂不能应用'
      result.proposal_id = result.proposal_id || data.proposal_id
      if (result.proposed_config) result.proposed_config = normalizeConfig(config)
      data.result = result
    }
  }
  if (!data.items && Array.isArray(data.plans)) data.items = data.plans
  if (data.plan) data.plan = normalizePlan(data.plan)
  if (route === 'previews') data.config = normalizeConfig(data.config)
  if (route === 'options')
    data.limits = {
      ...data.limits,
      max_date_span_days: (data.limits || {}).max_date_span_days || (data.limits || {}).max_span_days
    }
  if (route.includes('/sessions')) {
    const session = data.session || data
    session.items = rows(session.items).map((item) => {
      const receipt =
        rows(data.receipts)
          .filter((receipt) => text(receipt.task_item_id) === text(item.task_item_id))
          .pop() || {}
      let state = item.completion_state
      if (state === 'pending') state = 'not_started'
      if (state === 'done')
        state =
          receipt.plan_credit_status === 'credited'
            ? flag(receipt.answer_saved) && receipt.credit_id
              ? 'credited'
              : 'ledger_pending'
            : receipt.plan_credit_status === 'duplicate'
            ? 'duplicate_not_credited'
            : ['not_eligible', 'failed'].includes(receipt.plan_credit_status)
            ? 'rejected'
            : 'ledger_pending'
      return { ...item, context: item.context || item.learning_plan_context, completion_state: state }
    })
    if (
      !session.scope &&
      session.items.length &&
      session.items.every((item) => scopeKey(item.scope) === scopeKey(session.items[0].scope))
    )
      session.scope = session.items[0].scope
  }
  return data
}
export const planGet = async (route, params = {}) =>
  normalizeLearningResponse(await studyGet('learning-plans' + (route ? '/' + route : ''), params), route)
export const planPost = async (route, data) =>
  normalizeLearningResponse(await studyPost('learning-plans' + (route ? '/' + route : ''), data), route)
export const todayStats = (parent) =>
  send('/api/answer/getFirstStat', 'post', { identity_parent_id: parent }, true)
export const reportCategories = (parent) =>
  send('/api/answer/getCategory', 'post', { identity_parent_id: parent }, true)
export const loadReport = async (scope, period, date) =>
  validateReport(
    await studyGet('reports/resolve', { ...scope, period, anchor_date: date }),
    scope,
    period,
    date
  )
export const submitPlanAnswer = async (body) => {
  const reply = await send(
    '/api/v1/question/answer',
    'post',
    { answer: JSON.stringify([body]), remove: 0 },
    false,
    true
  )
  const receipts = rows(reply.learning_plan_receipts)
  if (receipts.length) {
    const receipt = receipts.find((item) => text(item.client_attempt_id) === text(body.client_attempt_id))
    if (
      !receipt ||
      !flag(receipt.answer_saved) ||
      ['not_eligible', 'failed', 'rejected'].includes(receipt.plan_credit_status)
    ) {
      const error = new Error(
        (receipt && flag(receipt.answer_saved)
          ? '答案已保存，但尚未计入计划，请返回计划更新进度'
          : '答案尚未计入计划，请返回计划刷新后重试') +
          (receipt && receipt.reason_code ? '（' + receipt.reason_code + '）' : '')
      )
      error.answerSaved = Boolean(receipt && flag(receipt.answer_saved))
      throw error
    }
  }
  return reply
}
export const submitPracticeAnswers = (data, kind = 'question') => {
  if (!['question', 'unit', 'autonomous'].includes(kind)) throw new Error('不支持的答题来源')
  return send('/api/v1/' + kind + '/answer', 'post', data, false, true)
}

export async function prepareSession(session, parent, planId, taskId) {
  const scope = session.scope || {},
    items = rows(session.items)
  if (
    text(session.plan_id) !== text(planId) ||
    text(session.task_id) !== text(taskId) ||
    (session.identity_parent_id && text(session.identity_parent_id) !== text(parent)) ||
    !session.session_id ||
    !scope.chapter_id ||
    scope.source !== 'question' ||
    (session.submission_mode && session.submission_mode !== 'embedded_v1') ||
    (session.answer_path && session.answer_path !== '/api/v1/question/answer') ||
    (session.launch_schema && session.launch_schema !== 'learning_task_v1') ||
    rows(session.unavailable_items).length
  )
    throw new Error('任务启动信息发生变化，请返回计划刷新')
  if (
    new Set(items.map((item) => item.task_item_id)).size !== items.length ||
    new Set(items.map((item) => item.ordinal)).size !== items.length
  )
    throw new Error('任务题目顺序无效')
  for (const item of items) {
    const context = item.context || {},
      question = item.question || {}
    if (
      scopeKey(item.scope) !== scopeKey(scope) ||
      context.context_version !== 'learning_task_v1' ||
      text(context.learning_plan_id) !== text(planId) ||
      text(context.learning_task_id) !== text(taskId) ||
      text(context.learning_task_item_id) !== text(item.task_item_id) ||
      text(context.learning_session_id) !== text(session.session_id) ||
      !context.revision_id ||
      !item.task_item_id ||
      !(question.question_id || item.question_id) ||
      !(question.question_version || item.question_version)
    )
      throw new Error('题目与任务范围不一致，请刷新任务')
  }
  const missing = items.filter(
    (item) =>
      item.completion_state === 'not_started' &&
      (!(item.question || {}).title ||
        (item.question || {}).reference_answer == null ||
        (item.question || {}).point == null)
  )
  if (!missing.length) return session
  const ids = missing.map((item) => text(item.question_id || (item.question || {}).question_id))
  if (ids.some((id) => !/^\d+$/.test(id))) throw new Error('任务题号不完整')
  let data = await send(
    '/api/question/getPointsQuestion',
    'post',
    { identity_id: scope.identity_id, question_id: JSON.stringify(ids) },
    true
  )
  if (typeof data === 'string') {
    try {
      const decoded = JSON.parse(EUtils.decode(data))
      data = JSON.parse(EUtils.decrypted(decoded.value, decoded.iv))
    } catch (_) {
      throw new Error('任务题目内容解码失败，请重试')
    }
  }
  const flat = []
  const flatten = (list, parentQuestion = {}) =>
    rows(list).forEach((raw) => {
      const q = {
        ...raw,
        identity_id: raw.identity_id || parentQuestion.identity_id,
        chapter_id: raw.chapter_id || parentQuestion.chapter_id,
        material: raw.material || parentQuestion.material,
        parent_id: raw.parent_id || parentQuestion.question_id || parentQuestion.id || '0'
      }
      flat.push(q)
      flatten(raw.sonQuestion || raw.children, q)
    })
  flatten(data)
  for (const item of missing) {
    const matches = flat.filter(
      (q) =>
        text(q.question_id || q.id) === text(item.question_id || (item.question || {}).question_id) &&
        text(q.identity_id) === text(scope.identity_id) &&
        text(q.chapter_id) === text(scope.chapter_id)
    )
    if (matches.length !== 1) throw new Error('任务题目缺失或重复')
    const raw = matches[0],
      version = item.question_version || (item.question || {}).question_version
    if (
      +raw.lock > 0 ||
      raw.deleted_at ||
      (raw.question_version && text(raw.question_version) !== text(version)) ||
      !raw.title ||
      !raw.answer
    )
      throw new Error('题目版本、章节或权限已变化，请返回计划刷新')
    item.question = {
      question_id: text(raw.question_id || raw.id),
      question_version: version,
      title: raw.title,
      material: raw.material,
      parent_id: raw.parent_id,
      point: raw.point,
      reference_answer: raw.answer,
      explanation: raw.explain,
      options: rows(raw.option).map((option) => ({
        original_key: option.key,
        text: option.title || option.text,
        option_id: option.option_id || option.id
      }))
    }
  }
  return session
}
